import React, { ChangeEvent } from 'react';

import {
  Box,
  Pagination as MUIPagination,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  colors,
  useMediaQuery,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import appTheme from '../../styles/themes/theme';

const PaginationSelect = ({
  disabled,
  value,
  onChange,
  options,
}: {
  value: number;
  onChange: (value: number) => void;
  options: number[];
  disabled?: boolean;
}) => {
  return (
    <Select
      disabled={disabled}
      variant="outlined"
      value={value}
      onChange={(e: SelectChangeEvent<number>) => onChange(Number(e.target.value))}
      IconComponent={ExpandMore}
      inputProps={{
        'aria-labelledby': 'rows-per-page-label',
      }}
      sx={{
        ml: 0.5,
        height: '44px',
        width: '72px',

        '& .MuiSelect-select': {
          '&:focus': {
            background: 'none',
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${colors.grey[300]} !important`,
          borderRadius: '8px',
        },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

interface Props {
  /** Rows on the current page (kept for caller compatibility; label uses page/pageSize/itemTotal) */
  itemCount: number;
  itemTotal: number;
  pageCount: number;
  onPageChange: (event: ChangeEvent<unknown>, page: number) => void;
  page: number;
  pageSize: number;
  onPageSizeChange: (newPageSize: number) => void;
  disabled?: boolean;
  pageSizeOptions?: number[];
}

/** 1-based page index; returns e.g. "Viewing 11-20 of 255" */
export const getViewingRangeLabel = (page: number, pageSize: number, itemTotal: number) => {
  if (itemTotal === 0) {
    return 'Viewing 0 of 0';
  }

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, itemTotal);

  if (start === end) {
    return `Viewing ${start} of ${itemTotal}`;
  }

  return `Viewing ${start}-${end} of ${itemTotal}`;
};

export const Pagination = ({
  itemTotal,
  pageCount,
  onPageChange,
  page,
  disabled,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 40, 50],
}: Props) => {
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));
  const viewingRangeLabel = getViewingRangeLabel(page, pageSize, itemTotal);

  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 1,
          boxShadow: '0px -5px 16px -4px rgba(16, 24, 40, 0.08)',
          border: '1px solid',
          borderColor: colors.grey[100],
        }}
      >
        <Stack gap={1}>
          <Typography variant="small">Rows per page</Typography>
          <PaginationSelect
            disabled={disabled}
            value={pageSize}
            onChange={onPageSizeChange}
            options={pageSizeOptions}
          />
          <Typography variant="small">{viewingRangeLabel}</Typography>
        </Stack>
        <Stack gap={1} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Typography sx={{ mr: 1 }} variant="small">
            Page
          </Typography>
          <PaginationSelect
            disabled={disabled}
            value={page}
            onChange={(value) => onPageChange({} as ChangeEvent<unknown>, value)}
            options={Array.from({ length: pageCount === 0 ? page : pageCount }, (_, i) => i + 1)}
          />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: { sm: 'column-reverse', md: 'row' },
        gap: 1,
        p: 1,
        boxShadow: '0px -5px 16px -4px rgba(16, 24, 40, 0.08)',
        border: '1px solid',
        borderColor: colors.grey[100],
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography id="rows-per-page-label" variant="large">
          Rows per page
        </Typography>
        <PaginationSelect
          disabled={disabled}
          value={pageSize}
          onChange={onPageSizeChange}
          options={pageSizeOptions}
        />
        <Typography sx={{ ml: 1.5 }} variant="large">
          {viewingRangeLabel}
        </Typography>
      </Box>
      <MUIPagination
        page={page}
        count={pageCount}
        shape="rounded"
        size="large"
        onChange={onPageChange}
        disabled={disabled}
        sx={{
          border: `1px solid ${colors.grey[300]}`,
          borderRadius: '8px',
          '& .MuiPaginationItem-page.Mui-disabled.Mui-selected': {
            border: 'none',
          },
          '& .Mui-disabled': {
            opacity: 0.5,
          },
          '& button': {
            margin: 0,
            borderRadius: '0px',
            borderLeft: '1px solid',

            borderColor: colors.grey[300],
          },
          '& ul li:first-of-type button': {
            border: 'none',
          },
          '& .MuiPaginationItem-ellipsis': {
            margin: 0,
            borderLeft: `1px solid ${colors.grey[300]}`,
            height: '40px',
            paddingTop: '8px',
            borderRadius: '0px',
          },
        }}
      />
    </Box>
  );
};
